import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { API_URLS } from "../../config";
import { apiRequest } from "../../utils/apiRequest";
import AccessDenied from "../../Components/AccessDenied";
import {HiOutlineWrenchScrewdriver} from "react-icons/hi2";
import { useToast } from "../../Components/Toast";
import {
  FiEdit, FiPlus, FiEye, FiTrash2, FiChevronUp, FiChevronDown, FiRefreshCw, FiCheck, FiX, FiUploadCloud
} from "react-icons/fi";
import MasterUploadModal from "../../Components/MasterUploadModal";
import FilterPanel, { FilterField } from "../../Components/FilterPanel";

/* ── helpers ─────────────────────────────────────────────────── */
const EMPTY_FORM = {
  name: "",
  disciplineId: "",
  subDisciplineId: "",
};

const Products = () => {
  const { token, user } = useAuth();
  const toast = useToast();

  /* list state */
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  /* selection state */
  const [selectedIds, setSelectedIds] = useState([]);
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);

  /* filter state */
  const [search, setSearch] = useState("");
  const [fDiscipline, setFDiscipline] = useState("");
  const [fSubDiscipline, setFSubDiscipline] = useState("");

  /* option state */
  const [disciplines, setDisciplines] = useState([]);
  const [subDisciplines, setSubDisciplines] = useState([]);

  /* modal state */
  const [modal, setModal] = useState(null); // "add" | "edit" | "view"
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  /* ── fetch options ─────────────────────────────────────────── */
  const fetchDisciplines = useCallback(async () => {
    const res = await apiRequest(`${API_URLS.DISCIPLINES}?limit=1000`, {}, token);
    if (res?.ok) setDisciplines(res.data.rows || []);
  }, [token]);

  const fetchSubDisciplines = useCallback(async (dId) => {
    if (!dId) { setSubDisciplines([]); return; }
    const res = await apiRequest(`${API_URLS.SUBDISCIPLINES}?disciplineId=${dId}&limit=1000`, {}, token);
    if (res?.ok) setSubDisciplines(res.data.rows || []);
  }, [token]);

  useEffect(() => { fetchDisciplines(); }, [fetchDisciplines]);

  /* ── fetch list ─────────────────────────────────────────────── */
  const fetchRows = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit });
    if (search) params.set("search", search);
    if (fDiscipline) params.set("disciplineId", fDiscipline);
    if (fSubDiscipline) params.set("subDisciplineId", fSubDiscipline);

    const result = await apiRequest(`${API_URLS.PRODUCTS}?${params}`, {}, token);
    if (!result) return;
    if (result.denied) { setDenied(result); setLoading(false); return; }
    if (result.ok) {
      setRows(result.data.rows || []);
      setTotal(result.data.total || 0);
      setSelectedIds([]);
    }
    setLoading(false);
  }, [token, page, search, fDiscipline, fSubDiscipline, limit]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  /* ── Selection ── */
  const toggleSelectRow = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === rows.length && rows.length > 0) setSelectedIds([]);
    else setSelectedIds(rows.map(r => r.id));
  };

  /* ── modal helpers ──────────────────────────────────────────── */
  const initialFormSnapshotRef = useRef(null);
  const openAdd = () => { setFormData(EMPTY_FORM); initialFormSnapshotRef.current = JSON.stringify(EMPTY_FORM); setModal("add"); };
  const openEdit = async () => {
    if (selectedIds.length !== 1) return;
    const item = rows.find(r => r.id === selectedIds[0]);
    if (item) {
      setFormData({ ...item });
      initialFormSnapshotRef.current = JSON.stringify({ ...item });
      await fetchSubDisciplines(item.disciplineId);
      setModal("edit");
    }
  };
  const openView = async () => {
    if (selectedIds.length !== 1) return;
    const item = rows.find(r => r.id === selectedIds[0]);
    if (item) {
      setFormData(item);
      await fetchSubDisciplines(item.disciplineId);
      setModal("view");
    }
  };
  const closeModal = () => { setModal(null); setFormData(EMPTY_FORM); setSubDisciplines([]); };

  // Unsaved-changes guard — only relevant for add/edit, never for read-only view
  const isFormDirty = () => modal !== "view" && initialFormSnapshotRef.current !== null && JSON.stringify(formData) !== initialFormSnapshotRef.current;
  const requestCloseModal = () => {
    if (isFormDirty()) {
      toast.confirm(
        "You have unsaved changes. Do you want to save before closing?",
        () => handleSave(),
        () => closeModal()
      );
    } else {
      closeModal();
    }
  };

  const handleFormChange = e => {
    const { name, value } = e.target;
    setFormData(f => ({ ...f, [name]: value }));
    if (name === "disciplineId") {
      setFormData(f => ({ ...f, subDisciplineId: "" }));
      fetchSubDisciplines(value);
    }
  };

  /* ── save ───────────────────────────────────────────────────── */
  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    const isEdit = modal === "edit";
    const url = isEdit ? `${API_URLS.PRODUCTS}/${selectedIds[0]}` : API_URLS.PRODUCTS;
    const method = isEdit ? "PUT" : "POST";
    const result = await apiRequest(url, { method, body: formData }, token);
    setSaving(false);
    if (result?.ok) {
      toast.success("Saved successfully!");
      closeModal();
      fetchRows();
    } else toast.error(result?.data?.message || "Failed");
  };

  /* ── delete ─────────────────────────────────────────────────── */
  const handleDelete = () => {
    if (selectedIds.length === 0) return;
    const msg = selectedIds.length === 1
      ? "Delete this product?"
      : `Delete ${selectedIds.length} selected products?`;

    toast.deleteConfirm(msg, async () => {
      setLoading(true);
      let successCount = 0;
      for (const id of selectedIds) {
        const res = await apiRequest(`${API_URLS.PRODUCTS}/${id}`, { method: "DELETE" }, token);
        if (res?.ok) successCount++;
      }
      setLoading(false);
      if (successCount > 0) {
        toast.success(`Deleted ${successCount} items.`);
        fetchRows();
      } else toast.error("Failed to delete items.");
    });
  };

  /* ── export ─────────────────────────────────────────────────── */
  const handleExport = async () => {
    const res = await fetch(`${API_URLS.PRODUCTS}/export`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Products_${Date.now()}.xlsx`;
      a.click();
    }
  };

  const pages = Math.ceil(total / limit);

  if (denied) return (
    <div className="vendor-page">
      <div className="management-card">
        <AccessDenied message={denied.message} required={denied.required} />
      </div>
    </div>
  );

  return (
    <div className="vendor-page">

      {/* ── Action Bar ── */}
      <div className="vendor-action-bar">
        <button className="new-user-btn" onClick={openAdd}><FiPlus /> New Product</button>
        <div style={{ width: 1, height: 24, background: "#ccc", margin: "0 8px" }} />
        <button className="action-btn view-btn" disabled={selectedIds.length !== 1} onClick={openView}><FiEye /> View</button>
        <button className="action-btn edit-btn" disabled={selectedIds.length !== 1} onClick={openEdit}><FiEdit /> Edit</button>
        <button className="action-btn delete-btn" disabled={selectedIds.length === 0} onClick={handleDelete}><FiTrash2 /> Delete</button>
        {['global_superadmin', 'company_admin'].includes(user?.role) && (
          <>
            <button className="action-btn export-btn" onClick={handleExport}><FiRefreshCw size={13} /> Export</button>
            <button className="action-btn upload-btn" onClick={() => setIsUploadOpen(true)}><FiUploadCloud /> Upload</button>
          </>
        )}
        <FilterPanel
          title="Filter products"
          width={320}
          columns={1}
          values={{ search, discipline: fDiscipline, subDiscipline: fSubDiscipline }}
          emptyValues={{ search: "", discipline: "", subDiscipline: "" }}
          onChange={(v) => { setSearch(v.search); setFDiscipline(v.discipline); setFSubDiscipline(v.subDiscipline); setPage(1); }}
          onOpen={(v) => { if (v.discipline) fetchSubDisciplines(v.discipline); }}
        >
          {(filters, set) => (
            <>
              <FilterField label="Search" wide>
                <input autoFocus placeholder="Search product..." value={filters.search}
                  onChange={(e) => set({ search: e.target.value })} />
              </FilterField>
              <FilterField label="Discipline">
                <select value={filters.discipline}
                  onChange={(e) => { set({ discipline: e.target.value, subDiscipline: "" }); fetchSubDisciplines(e.target.value); }}>
                  <option value="">All Disciplines</option>
                  {disciplines.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </FilterField>
              <FilterField label="Sub-Discipline">
                <select value={filters.subDiscipline} onChange={(e) => set({ subDiscipline: e.target.value })} disabled={!filters.discipline}>
                  <option value="">All Sub-Disciplines</option>
                  {subDisciplines.map(sd => <option key={sd.id} value={sd.id}>{sd.name}</option>)}
                </select>
              </FilterField>
            </>
          )}
        </FilterPanel>
      </div>

      {/* ── Table Card ── */}
      <div className="management-card">
        <div className="management-header" onClick={() => setIsTableCollapsed(!isTableCollapsed)}>
          <div className="header-title">
            <HiOutlineWrenchScrewdriver size={18} />
            <h2>Product Master</h2>
            <span className="vendor-badge">{total}</span>
          </div>
          <div>{isTableCollapsed ? <FiChevronDown /> : <FiChevronUp />}</div>
        </div>

        {!isTableCollapsed && (
          <div className="vendor-table-container">
            {loading && rows.length === 0 ? <div style={{ padding: 20 }}>Loading...</div> : (
              <table className="vendor-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        checked={rows.length > 0 && selectedIds.length === rows.length}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th style={{ width: 80 }}>SR. NO.</th>
                    <th>PRODUCT NAME</th>
                    <th>DISCIPLINE</th>
                    <th>SUB-DISCIPLINE</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: "center", padding: 40 }}>No products found</td></tr>
                  ) : rows.map((r, i) => (
                    <tr
                      key={r.id || i}
                      className={selectedIds.includes(r.id) ? "selected" : ""}
                      onClick={() => toggleSelectRow(r.id)}
                    >
                      <td onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(r.id)}
                          onChange={() => toggleSelectRow(r.id)}
                        />
                      </td>
                      <td>{r.srNo}</td>
                      <td>{r.name}</td>
                      <td>{r.disciplineName}</td>
                      <td>{r.subDisciplineName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Pagination */}
            {total > 0 && (
              <div className="cust-pagination">
                <select
                  value={limit}
                  onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                  style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}
                >
                  {[25, 50, 75, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="cust-page-info">Page {page} of {Math.max(1, pages)}</span>
                <button className="cust-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                <button className="cust-btn" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</button>
              </div>
            )}
          </div>
        )}
      </div>

      {modal && (
        <div className="vendor-modal-overlay" onClick={requestCloseModal}>
          <div className="vendor-modal" onClick={e => e.stopPropagation()} style={{ width: "85%", maxWidth: "600px" }}>
            <div style={{ background: "#1a2a4d", color: "#fff", padding: "12px 30px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 600 }}>{modal.toUpperCase()} PRODUCT</span>
              <button onClick={requestCloseModal} style={{ background: "none", border: "none", color: "#fff", fontSize: 20, display: "flex", cursor: "pointer" }}><FiX /></button>
            </div>
            <div style={{ padding: 30 }}>
              <form onSubmit={handleSave}>
                <div className="vendor-view-grid">
                  <div className="vendor-view-field vendor-full">
                    <label className="vendor-view-label">Product Name *</label>
                    <input name="name" className="cust-input" value={formData.name} onChange={handleFormChange} required disabled={modal === 'view'} style={{}} />
                  </div>

                  <div className="vendor-view-field">
                    <label className="vendor-view-label">Discipline *</label>
                    <select name="disciplineId" className="cust-input" value={formData.disciplineId} onChange={handleFormChange} required disabled={modal === 'view'}>
                      <option value="">Select Discipline</option>
                      {disciplines.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  <div className="vendor-view-field">
                    <label className="vendor-view-label">Sub-Discipline *</label>
                    <select name="subDisciplineId" className="cust-input" value={formData.subDisciplineId} onChange={handleFormChange} required disabled={modal === 'view' || !formData.disciplineId}>
                      <option value="">Select Sub-Discipline</option>
                      {subDisciplines.map(sd => <option key={sd.id} value={sd.id}>{sd.name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-footer" style={{ marginTop: 30, textAlign: "right", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  {modal !== 'view' && (
                    <button type="submit" className="save-btn" disabled={saving}><FiCheck /> Save</button>
                  )}
                  <button type="button" className="cancel-btn" onClick={requestCloseModal}>{modal === 'view' ? "Close" : "Cancel"}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      <MasterUploadModal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title="Upload Product"
        templateUrl={`${API_URLS.PRODUCTS}/template`}
        uploadUrl={`${API_URLS.PRODUCTS}/upload`}
        instructions={[
          "Product Name, Discipline Name, and Sub-Discipline Name are mandatory fields.",
          "Parent entities must match existing records exactly."
        ]}
        onSuccess={fetchRows}
        token={token}
      />
    </div>
  );
};

export default Products;
